import {
    CheckIcon,
    CircularProgress,
    Dialog,
    List,
    ListItem,
    Stack,
    Typography,
} from '@ds/coolshop';
import { PriceListInfo } from '@src/__generated__/types';
import EditNodeRefetchContext from '@src/components/hierarchies/shared/EditNodeRefetchContext';
import SnackBarUtils from '@src/components/shared/SnackBarUtils';
import {
    useChangeGlPriceListMutation,
    useGetCompatiblePriceListsQuery,
} from '@src/utils/cgt/cgtQuery.generated';
import { Dispatch, SetStateAction, useContext, useState } from 'react';
import { useIntl } from 'react-intl';

interface IChangeGlPriceListDialogProps {
    open: boolean;
    onClose: () => void;
    modelId: string;
    glId: string;
}

const CompatiblePriceLists = ({
    priceLists,
    title,
    selectedPriceList,
    setSelectedPriceList,
}: {
    priceLists: PriceListInfo[] | null | undefined;
    title: string;
    selectedPriceList: string | null;
    setSelectedPriceList: Dispatch<SetStateAction<string | null>>;
}) => {
    if (!priceLists) return null;
    const intl = useIntl();

    const handleSelect = (priceListId: string) => {
        if (selectedPriceList === priceListId) {
            setSelectedPriceList(null);
        } else {
            setSelectedPriceList(priceListId);
        }
    };

    return (
        <Stack gap={2}>
            <Typography variant="h6">{title}</Typography>
            {priceLists.length === 0 ? (
                <Typography variant="subtitle2">
                    {intl.formatMessage({
                        id: 'change-price-list.no-price-lists',
                        defaultMessage: 'Nessun listino compatibile trovato',
                    })}
                </Typography>
            ) : (
                <>
                    <Typography variant="subtitle2" mt={-2}>
                        {intl.formatMessage({
                            id: 'change-price-list.subtitle',
                            defaultMessage: 'Seleziona il listino da utilizzare',
                        })}
                    </Typography>
                    <List>
                        {priceLists?.map(p => (
                            <ListItem
                                selected={selectedPriceList === p.id}
                                onClick={() => handleSelect(p.id)}
                                cursor="pointer"
                                key={p.id}
                                endAdornment={
                                    selectedPriceList === p.id ? (
                                        <CheckIcon color="primary" />
                                    ) : undefined
                                }
                            >
                                {p.listName}
                            </ListItem>
                        ))}
                    </List>
                </>
            )}
        </Stack>
    );
};

export const ChangeGlPriceListDialog = (props: IChangeGlPriceListDialogProps) => {
    const { open, onClose, modelId, glId } = props;
    const intl = useIntl();

    const [compatiblePriceLists, setCompatiblePriceLists] = useState<
        PriceListInfo[] | null | undefined
    >();
    const [compatiblePriceListsZMAB, setCompatiblePriceListsZMAB] = useState<
        PriceListInfo[] | null | undefined
    >();
    const [disabledPriceListId, setDisabledPriceListId] = useState<string | null | undefined>();

    const [selectedPriceList, setSelectedPriceList] = useState<string | null>(null);
    const [selectedPriceListZMAB, setSelectedPriceListZMAB] = useState<string | null>(null);

    const refetchNode = useContext(EditNodeRefetchContext);

    const { loading } = useGetCompatiblePriceListsQuery({
        variables: { glId, modelId },
        skip: !open,
        fetchPolicy: 'network-only',
        onCompleted: data => {
            setCompatiblePriceLists(data?.getCompatiblePriceLists?.compatibilePriceLists);
            setCompatiblePriceListsZMAB(data?.getCompatiblePriceLists?.compatibilePriceListsZMAB);
            setDisabledPriceListId(data?.getCompatiblePriceLists?.disabledPriceListId);
        },
    });

    const [changeGlPriceList, { loading: changeGlPriceListLoading }] =
        useChangeGlPriceListMutation();

    const handleClose = () => {
        onClose();
        setSelectedPriceList(null);
        setSelectedPriceListZMAB(null);
    };

    const handleConfirm = () => {
        changeGlPriceList({
            variables: {
                modelId,
                glId,
                disabledPriceListId,
                priceListId: selectedPriceList,
                priceListIdZMAB: selectedPriceListZMAB,
            },
            onCompleted: () => {
                SnackBarUtils.success(
                    intl.formatMessage({
                        id: 'change-price-list.success',
                        defaultMessage: 'Listino cambiato con successo',
                    }),
                );
                handleClose();
                refetchNode();
            },
        });
    };

    return (
        <Dialog
            open={open}
            header={{
                title: intl.formatMessage({
                    id: 'change-price-list.title',
                    defaultMessage: 'Cambia listino',
                }),
            }}
            actions={[
                {
                    label: intl.formatMessage({
                        id: 'change-price-list.button-cancel',
                        defaultMessage: 'Annulla',
                    }),
                    disabled: changeGlPriceListLoading,
                    invertedColor: true,
                    onClick: handleClose,
                },
                {
                    label: intl.formatMessage({
                        id: 'change-price-list.button-confirm',
                        defaultMessage: 'Conferma',
                    }),
                    disabled:
                        (!selectedPriceList && !selectedPriceListZMAB) || changeGlPriceListLoading,
                    onClick: handleConfirm,
                },
            ]}
            fullWidth
        >
            <Stack gap={1} alignItems="center" justifyContent="center" height="100%">
                {loading ? (
                    <CircularProgress />
                ) : (
                    <Stack gap={1} width="100%">
                        {!compatiblePriceLists && !compatiblePriceListsZMAB ? (
                            <Typography variant="subtitle2">
                                {intl.formatMessage({
                                    id: 'change-price-list.no-price-lists',
                                    defaultMessage:
                                        'Gli item disabilitati di questo GL fanno riferimento a più di un listino. Non è possibile fornire un elenco di listini compatibili e pertanto è richiesto un intervento manuale',
                                })}
                            </Typography>
                        ) : (
                            <>
                                <CompatiblePriceLists
                                    priceLists={compatiblePriceLists}
                                    title={intl.formatMessage({
                                        id: 'change-price-list.title-compatibile',
                                        defaultMessage: 'Listini compatibili',
                                    })}
                                    selectedPriceList={selectedPriceList}
                                    setSelectedPriceList={setSelectedPriceList}
                                />
                                <CompatiblePriceLists
                                    priceLists={compatiblePriceListsZMAB}
                                    title={intl.formatMessage({
                                        id: 'change-price-list.title-compatible-zmab',
                                        defaultMessage: 'Listini compatibili ZMAB',
                                    })}
                                    selectedPriceList={selectedPriceListZMAB}
                                    setSelectedPriceList={setSelectedPriceListZMAB}
                                />
                            </>
                        )}
                    </Stack>
                )}
            </Stack>
        </Dialog>
    );
};
